import { BASE_URL, PROFILES } from "#next-seo.config";
import { ProfilePageJsonLd } from "next-seo";
import { generateNextSeo } from "next-seo/pages";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import { useEffect } from "react";
import useSWR from "swr";
import Subscribe from "~/components/Subscribe";
import { WorkList } from "~/components/WorkList";
import { apiUrl } from "~/config";
import { type PostCard, toPostCard } from "~/lib/blog";
import { getBlogDrafts } from "~/lib/blog-drafts";
import fetcher from "~/lib/fetcher";
import { getAllFilesFrontMatter } from "~/lib/mdx";
import BlogPost from "../components/BlogPost";
import Container from "../components/Container";
import { Divider, History } from "../components/History";

const url = BASE_URL;
const title = "About Samuel Batista";
const description = "Learn about me and my work.";

export async function getStaticProps() {
  const [portableSecret] = await Promise.all([import("./work/open-source/portable-secret")]);

  // Drafts join in development only, as they do on the blog index, so the newest one shows here tagged as such.
  const posts = [...(await getAllFilesFrontMatter("blog")), ...(await getBlogDrafts())];
  let latestPost = undefined;
  for (const post of posts) {
    if (!latestPost || post.publishedAt >= latestPost.publishedAt) {
      latestPost = post;
    }
  }

  return {
    props: {
      posts: posts.map(toPostCard),
      latestPost: toPostCard(latestPost),
      portableSecret: {
        title: portableSecret.title,
        url: portableSecret.url,
        description: portableSecret.description
      }
    }
  };
}

export default function Home({
  posts,
  latestPost,
  portableSecret
}: {
  posts: PostCard[];
  latestPost: PostCard;
  portableSecret: { title: string; url: string; description: string };
}) {
  const { data, error } = useSWR(`${apiUrl}/api/blog/most_viewed`, fetcher);
  // The API names the post; this build's own frontmatter describes it, so its card matches the blog index's.
  const mostViewed: PostCard = data?.post
    ? (posts.find(({ slug, isDraft }) => slug === data.post.slug && !isDraft) ?? toPostCard(data.post))
    : { slug: "", title: "Loading...", summary: "Loading..." };

  useEffect(() => {
    if (error) {
      console.error(error);
    }
  }, [error]);

  return (
    <Container>
      <Head>
        {generateNextSeo({
          title,
          description,
          canonical: url,
          openGraph: {
            url,
            title,
            description
          }
        })}
      </Head>
      <ProfilePageJsonLd
        mainEntity={{
          "@type": "Person",
          name: "Samuel Batista",
          url,
          image: `${BASE_URL}/images/avatar.jpg`,
          sameAs: Object.values(PROFILES)
        }}
      />
      <div className="flex flex-col justify-center items-start max-w-2xl mx-auto mb-4">
        <div
          className="flex justify-left"
          style={{
            overflow: "visible",
            paddingBottom: "2rem",
            paddingTop: "0.5rem"
          }}
        >
          <Image
            className="rounded-full ring-4"
            src="/images/avatar.webp"
            alt="Picture of Samuel Batista"
            width={138}
            height={138}
          />
        </div>
        <h1 className="font-bold text-3xl md:text-5xl tracking-tight mb-4 text-black dark:text-white">
          Samuel Batista
        </h1>
        <br />
        <h2 className="text-gray-600 dark:text-gray-400 mb-4">
          Hi there, welcome to my corner of the internet 👋
          <br />
          <br />I was born and raised in Portugal and immigrated to the US as a teenager to pursue my dreams. After
          graduating from Full Sail University with a degree in Game Development, I spent nearly a decade developing
          games at{" "}
          <a href="https://firaxis.com/" target="__blank">
            Firaxis
          </a>{" "}
          and{" "}
          <a href="https://bighugegames.com/" target="__blank">
            Big Huge Games
          </a>
          . When games stopped teaching me new things, I taught myself the web and spent years building complex
          applications with React, Node and TypeScript.
          <br />
          <br />
          These days I work with AI to build{" "}
          <a href="https://mcpmanager.ai" target="__blank">
            MCP Manager
          </a>
          , a product that helps businesses secure and monitor how AIs and humans connect to their systems of record. On
          the side I run{" "}
          <a href="https://dataconnector-pro.com/" target="__blank">
            DataConnector-Pro
          </a>
          , a small consulting shop where I use AI to help businesses modernize legacy workflows that off-the-shelf
          tools can&apos;t reach.
          <br />
          <br />I love programming, AI, open source software, video games, economics, teaching & learning.
          <br />
          <br />
          Enjoy your visit!
        </h2>
        <Divider />
        <h3 className="font-bold text-2xl md:text-4xl tracking-tight mb-4 mt-8 text-black dark:text-white">
          Latest Post
        </h3>
        <BlogPost post={latestPost} />
        <h3 className="font-bold text-2xl md:text-4xl tracking-tight mb-4 mt-8 text-black dark:text-white">
          Most Viewed Post
        </h3>
        <BlogPost post={mostViewed} views={data?.views} />
        <Subscribe />
        <Link
          href="/blog"
          className="underline flex items-center text-sm my-4 mx-auto px-4 py-2 rounded-md font-medium text-gray-900 dark:text-gray-100"
        >
          All Posts
        </Link>
        <Divider />
        <h3 className="font-bold text-2xl md:text-4xl tracking-tight mb-4 mt-8 text-black dark:text-white">Work</h3>
        <WorkList portableSecret={portableSecret} />
        <Divider />
        <History />
      </div>
    </Container>
  );
}
